'use client';

import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';
import { useIntegrationStatus } from '@/hooks/useIntegrationStatus';

const PMUserMappingModal = dynamic(() =>
  import('./modals/PMUserMappingModal').then((m) => m.PMUserMappingModal)
);
const SlackSetupModal = dynamic(() =>
  import('./modals/SlackSetupModal').then((m) => m.SlackSetupModal)
);

/** Only OAuth integrations land here — API-key integrations (Sentry, BetterStack, etc.) use EKUSetupModal directly. */
const APP_NAME_CONFIG: Record<
  string,
  | {
      type: 'pm';
      toolType: 'linear' | 'jira' | 'asana' | 'zendesk';
      label: string;
    }
  | { type: 'slack' }
> = {
  linear: { type: 'pm', toolType: 'linear', label: 'Linear' },
  jira: { type: 'pm', toolType: 'jira', label: 'Jira' },
  asana: { type: 'pm', toolType: 'asana', label: 'Asana' },
  zendesk: { type: 'pm', toolType: 'zendesk', label: 'Zendesk' },
  slack: { type: 'slack' },
};

/**
 * Reads `?appName=...` from URL after OAuth return and opens the right mapping modal.
 * Clears the param after opening so refresh doesn't re-trigger.
 */
export function HomeOAuthReturnHandler() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { refresh: refreshStatus } = useIntegrationStatus();
  const appName = searchParams.get('appName')?.toLowerCase() ?? '';

  const [pmModal, setPmModal] = useState<{
    toolType: 'linear' | 'jira' | 'asana' | 'zendesk';
    label: string;
  } | null>(null);
  const [slackOpen, setSlackOpen] = useState(false);
  // Idempotency guard: React 19 strict-mode double-invokes effects and fast
  // refresh can re-run this before the URL is actually stripped. Without this,
  // a transient appName=... could call router.replace twice in a frame —
  // Firefox enforces a hard ~100/30s cap on history mutations.
  const handledAppNameRef = useRef<string | null>(null);

  useEffect(() => {
    if (!appName) return;
    const config = APP_NAME_CONFIG[appName];
    if (!config) return;
    if (handledAppNameRef.current === appName) return;
    handledAppNameRef.current = appName;

    // Clear the query param so refresh won't re-trigger
    const url = new URL(window.location.href);
    url.searchParams.delete('appName');
    router.replace(url.pathname + url.search, { scroll: false });

    // Open the right modal
    if (config.type === 'pm') {
      setPmModal({ toolType: config.toolType, label: config.label });
    } else if (config.type === 'slack') {
      setSlackOpen(true);
    }
  }, [appName, router]);

  return (
    <>
      <PMUserMappingModal
        open={!!pmModal}
        onOpenChange={(v) => {
          if (!v) setPmModal(null);
        }}
        toolType={pmModal?.toolType ?? 'linear'}
        toolLabel={pmModal?.label ?? ''}
        onComplete={refreshStatus}
      />
      <SlackSetupModal
        open={slackOpen}
        onOpenChange={setSlackOpen}
        onComplete={refreshStatus}
      />
    </>
  );
}
